/* This file holds shared refinance functions and
components for the auto refinance calculators */

import styles from "./refinance.module.css"
import React from "react"
import * as util from "activities/util"
import * as shared from "activities/components/shared"
import * as inputs from "activities/components/inputs"
import Line from "story/components/graphs/line"
import * as graphUtil from "shared/graphs/util"
import * as loan from "shared/loan-payoff"

export const graphDataAuto = (balance, currentMonthly, currentRate, refinanceTerm, refinanceRate) => {
  const currentTerm = balance / currentMonthly
  const months = currentTerm > refinanceTerm ? currentTerm : refinanceTerm
  const refinanceMonthly = loan.calculatePayment(balance, refinanceRate, refinanceTerm)
  let interestCurrent = 0
  let interestRefinance = 0
  const data = Array.from(Array(Math.ceil(months + 12))).reduce((memo, x, idx) => {
    if (idx === 0) memo.push([idx, balance, balance])
    else {
      const last = memo[memo.length - 1]
      const currentLast = last[1]
      const refinanceLast = last[2]
      const currentPrincipal = loan.getPrincipalPayment(currentLast, currentMonthly, currentRate)
      const refinancePrincipal = loan.getPrincipalPayment(refinanceLast, refinanceMonthly, refinanceRate)
      interestCurrent += currentMonthly - currentPrincipal
      interestRefinance += refinanceMonthly - refinancePrincipal
      memo.push([idx, loan.minusPayment(currentLast, currentPrincipal), loan.minusPayment(refinanceLast, refinancePrincipal)])
    }
    return memo
  }, [])
  return {data, interestCurrent, interestRefinance, refinanceMonthly}
}

export const getYears = (data) =>
  data.reduce((memo, item, idx) => {
    if (idx % 12 === 0) {
      memo.push([idx / 12, item[1], item[2]])
    }
    return memo
  }, [])

export const validateData = (isDisabled, graphDataFn) => {
  if (isDisabled) {
    return {
      data: [],
      interestCurrent: 0,
      interestRefinance: 0,
      refinanceMonthly: 0
    }
  } else {
    return graphDataFn()
  }
}

export const Graph = ({props, graphData, isDisabled}) => {
  const {theme, ariaLabel} = props
  return (
    <>
      {!isDisabled && (
        <Line
          ariaLabel={ariaLabel}
          yAxis="Loan Amount"
          values={loan.getYears(graphData)}
          colors={["#5c5761", graphUtil.getColor(theme, 1)]}
          type="calculator"
          tooltip={["Year", {name: "Current", type: "currency", color: "#5c5761"}, {name: "Refinanced", type: "currency", color: graphUtil.getColor(theme, 1)}]}
        />
      )}
      {isDisabled && <div className={styles["disable-message"]}>For the graph to display the current monthly payment must have a value greater than zero.</div>}
    </>
  )
}

export const Inputs = ({props, state, updateState}) => {
  const {balance, currentMonthly, currentRate, refinanceTerm, refinanceRate, resetKey} = state
  const {theme, interestRate, id} = props
  return (
    <div className={styles["inputs"]}>
      <div>
        <inputs.Label className="overline">Current Auto-Loan</inputs.Label>
        <div className={styles["inputs-group"]}>
          <inputs.Input id={id} title="Balance" onChange={updateState("balance", util.fromString)} value={balance} />
          <inputs.Input id={id} title="Monthly Payment" onChange={updateState("currentMonthly", util.fromString)} value={currentMonthly} />
          <inputs.RangeInput
            id={id}
            title="Interest Rate"
            onChange={updateState("currentRate", parseFloat)}
            initialValue={currentRate}
            min={interestRate.min}
            max={interestRate.max}
            step={interestRate.step}
            units="%"
            valueType="percent"
            resetKey={resetKey}
            color={theme["color-1"]}
          />
        </div>
      </div>
      <div>
        <inputs.Label className="overline">Refinanced Auto-Loan</inputs.Label>
        <div className={styles["inputs-group"]}>
          <inputs.Input id={id} title="Refinanced Term (Months)" onChange={updateState("refinanceTerm", util.fromString)} value={refinanceTerm} valueType="number" />
          <inputs.RangeInput
            id={id}
            title="Refinance Interest Rate"
            onChange={updateState("refinanceRate", parseFloat)}
            initialValue={refinanceRate}
            min={interestRate.min}
            max={interestRate.max}
            step={interestRate.step}
            units="%"
            valueType="percent"
            resetKey={resetKey}
            color={theme["color-1"]}
          />
        </div>
      </div>
    </div>
  )
}

export const InputsAndGraph = ({props, state, updateState, graphData, isDisabled}) => {
  const {balance, currentMonthly, currentRate, refinanceTerm, refinanceRate, resetKey} = state
  const {theme, interestRate, id} = props
  return <></>
}
